import {
  createMatch,
  tick,
  type CombatEvent,
  type HeroClassId,
  type Unit,
  type World,
} from "rite/src/sim/index";
import type {
  ActiveAction,
  ActiveImpact,
  PresentationEvent,
  PresentationFrame,
  SampledEntity,
  Vec3,
} from "../core";
import type { PresentationSource } from "./types";

const ARENA_CENTER_X = 14;
const ARENA_CENTER_Y = 9;
const WORLD_SCALE = 0.5;
const EVENT_HISTORY_LIMIT = 96;

function scenePosition(x: number, y: number, height = 0): Vec3 {
  return {
    x: (x - ARENA_CENTER_X) * WORLD_SCALE,
    y: height,
    z: (y - ARENA_CENTER_Y) * WORLD_SCALE,
  };
}

function teamName(team: 0 | 1 | null): "player" | "enemy" | "neutral" {
  return team === 0 ? "player" : team === 1 ? "enemy" : "neutral";
}

function archetypeFor(unit: Unit): string {
  if (["warrior", "paladin", "paragon"].includes(unit.classId)) return "shieldbearer";
  if (unit.classId === "ranger") return "ranger";
  if (["assassin", "dervish"].includes(unit.classId)) return "duelist";
  return "caster";
}

function tagsFor(unit: Unit, action: string): string[] {
  const tags: string[] = [];
  if (["priest", "cultist", "psychic", "ritualist"].includes(unit.classId)) tags.push("spell");
  if (/guard|shield|ward|sanctuary/i.test(action)) tags.push("shield");
  if (unit.classId === "ranger") tags.push("projectile");
  return tags;
}

/**
 * Runs Court's canonical deterministic simulation and adapts its current state
 * into Presentation IR. The adapter owns no combat rules and never predicts an
 * outcome; it only describes state that Court has already produced.
 */
export class CourtLiveSource implements PresentationSource {
  readonly id = "live:court-ai-match";
  readonly title = "Court · Live AI match";
  readonly mode = "live" as const;
  readonly canSeek = false;

  private world: World;
  private readonly history: PresentationEvent[] = [];
  private readonly eventIds = new WeakMap<CombatEvent, string>();
  private readonly seenUnitIds = new Set<number>();
  private readonly activeAbilities = new Map<number, string>();
  private readonly statusSnapshot = new Map<number, Set<string>>();
  private eventSequence = 0;

  constructor(
    private readonly classId: HeroClassId = "warrior",
    private readonly seed = 20260816,
  ) {
    this.world = createMatch({ classId, seed });
    this.captureInitialState();
  }

  get time(): number { return this.world.time; }
  get duration(): number { return this.world.duration; }
  get events(): readonly PresentationEvent[] { return this.history; }
  get sourceLabel(): string {
    return `Court simulation · seed ${this.seed} · ${Math.round(this.world.scores[0])}–${Math.round(this.world.scores[1])}`;
  }

  reset(): void {
    this.world = createMatch({ classId: this.classId, seed: this.seed });
    this.history.length = 0;
    this.seenUnitIds.clear();
    this.activeAbilities.clear();
    this.statusSnapshot.clear();
    this.eventSequence = 0;
    this.captureInitialState();
  }

  step(deltaSeconds: number): void {
    if (this.world.over) return;
    tick(this.world, Math.max(0, deltaSeconds), []);
    this.captureSemanticChanges();
  }

  seek(_time: number): void {
    // A live authority is intentionally not rewound. Restart creates a fresh,
    // deterministic match; recorded sources remain freely scrubbable.
  }

  sample(): PresentationFrame {
    const entities = this.sampleEntities();
    const recentEvents = this.history.filter((event) => this.time - event.at <= 1.05);
    return {
      time: this.time,
      duration: this.duration,
      entities,
      impacts: this.sampleImpacts(),
      recentEvents,
    };
  }

  private captureInitialState(): void {
    for (const unit of this.world.units) {
      this.seenUnitIds.add(unit.id);
      this.statusSnapshot.set(unit.id, new Set(unit.statuses.map((status) => status.id)));
      this.addEvent({
        id: this.nextEventId("spawn"),
        type: "spawn",
        entityId: this.unitId(unit.id),
        at: 0,
        position: scenePosition(unit.pos.x, unit.pos.y),
      });
    }
  }

  private captureSemanticChanges(): void {
    for (const unit of this.world.units) {
      if (!this.seenUnitIds.has(unit.id)) {
        this.seenUnitIds.add(unit.id);
        this.addEvent({
          id: this.nextEventId("spawn"),
          type: "spawn",
          entityId: this.unitId(unit.id),
          at: this.time,
          position: scenePosition(unit.pos.x, unit.pos.y),
        });
      }

      const ability = unit.casting?.abilityId ?? unit.charging?.abilityId;
      if (ability && this.activeAbilities.get(unit.id) !== ability) {
        this.activeAbilities.set(unit.id, ability);
        this.addEvent({
          id: this.nextEventId("act"),
          type: "act",
          entityId: this.unitId(unit.id),
          targetId: unit.casting?.targetId == null ? undefined : this.unitId(unit.casting.targetId),
          action: ability,
          at: this.time,
          phases: { telegraph: unit.casting?.total ?? 0.2, commit: 0.12, impact: 0.08, recover: 0.25 },
          tags: tagsFor(unit, ability),
        });
      } else if (!ability) {
        this.activeAbilities.delete(unit.id);
      }

      const previous = this.statusSnapshot.get(unit.id) ?? new Set<string>();
      const current = new Set<string>(unit.statuses.map((status) => status.id));
      for (const status of current) {
        if (!previous.has(status)) this.addStatusEvent(unit, status, true);
      }
      for (const status of previous) {
        if (!current.has(status)) this.addStatusEvent(unit, status, false);
      }
      this.statusSnapshot.set(unit.id, current);
    }

    for (const event of this.world.events) {
      if (this.eventIds.has(event)) continue;
      const id = this.nextEventId(event.kind);
      this.eventIds.set(event, id);
      if (!["dmg", "crit", "heal", "block", "miss", "kd", "interrupt"].includes(event.kind)) continue;
      const target = this.nearestUnit(event.x, event.y);
      if (!target) continue;
      const source = this.nearestSource(target, event.kind === "heal");
      const outcome = event.kind === "heal" ? "heal" : event.kind === "block" ? "block" : event.kind === "miss" ? "miss" : "hit";
      this.addEvent({
        id,
        type: "impact",
        sourceId: this.unitId(source?.id ?? target.id),
        targetId: this.unitId(target.id),
        at: Math.max(0, this.time - event.age),
        outcome,
        amount: event.value || undefined,
        effect: event.text || event.kind,
      });
    }
  }

  private addStatusEvent(unit: Unit, status: string, active: boolean): void {
    this.addEvent({
      id: this.nextEventId("status"),
      type: "status",
      entityId: this.unitId(unit.id),
      status,
      active,
      at: this.time,
    });
  }

  private addEvent(event: PresentationEvent): void {
    this.history.push(event);
    if (this.history.length > EVENT_HISTORY_LIMIT) this.history.splice(0, this.history.length - EVENT_HISTORY_LIMIT);
  }

  private nextEventId(kind: string): string {
    this.eventSequence += 1;
    return `court-${kind}-${this.eventSequence}`;
  }

  private unitId(id: number): string {
    return `court-unit-${id}`;
  }

  private nearestUnit(x: number, y: number): Unit | undefined {
    return this.world.units.reduce<Unit | undefined>((nearest, unit) => {
      if (!nearest) return unit;
      const distance = Math.hypot(unit.pos.x - x, unit.pos.y - y);
      const nearestDistance = Math.hypot(nearest.pos.x - x, nearest.pos.y - y);
      return distance < nearestDistance ? unit : nearest;
    }, undefined);
  }

  private nearestSource(target: Unit, allied: boolean): Unit | undefined {
    return this.world.units
      .filter((unit) => unit.id !== target.id && (allied ? unit.team === target.team : unit.team !== target.team))
      .sort((a, b) => Math.hypot(a.pos.x - target.pos.x, a.pos.y - target.pos.y) - Math.hypot(b.pos.x - target.pos.x, b.pos.y - target.pos.y))[0];
  }

  private sampleEntities(): SampledEntity[] {
    const units: SampledEntity[] = this.world.units.map((unit) => ({
      id: this.unitId(unit.id),
      label: unit.name,
      kind: "actor",
      archetype: archetypeFor(unit),
      presentationKit: unit.classId === "warrior" ? "court.warrior.v1" : undefined,
      team: teamName(unit.team),
      position: scenePosition(unit.pos.x, unit.pos.y),
      // Court's 0.42-unit collision radius maps to 0.21 scene units. A 0.52
      // figure scale keeps the rendered silhouette aligned with that footprint.
      scale: unit.kind === "minion" || unit.kind === "spirit" ? 0.42 : 0.52,
      anchors: {
        hud: { x: 0, y: unit.kind === "minion" || unit.kind === "spirit" ? 1.42 : 1.78, z: 0 },
      },
      vitals: {
        health: Math.max(0, unit.hp),
        maxHealth: Math.max(1, unit.maxHp),
        shield: unit.shields.reduce((sum, shield) => sum + shield.amount, 0),
      },
      visible: !(unit.dead && unit.kind !== "corpse"),
      movement: unit.movedThisTick ? { gait: unit.charging ? "dash" : "run", progress: this.time % 1 } : null,
      action: this.activeAction(unit),
      statuses: unit.statuses.map((status) => status.id),
    }));

    const cover: SampledEntity[] = this.world.cover.map((item) => ({
      id: `court-cover-${item.id}`,
      label: "Cover",
      kind: "prop",
      archetype: "cover",
      team: "neutral",
      position: scenePosition(item.x + item.w / 2, item.y + item.h / 2),
      dimensions: {
        x: Math.max(0.38, item.w * WORLD_SCALE),
        y: 0.62,
        z: Math.max(0.38, item.h * WORLD_SCALE),
      },
      visible: !item.destroyed,
      movement: null,
      action: null,
      statuses: [],
    }));

    const stones: SampledEntity[] = this.world.stones.map((stone) => ({
      id: `court-stone-${stone.id}`,
      label: stone.name,
      kind: "prop",
      archetype: "wardstone",
      team: teamName(stone.owner),
      position: scenePosition(stone.pos.x, stone.pos.y),
      scale: 0.48,
      visible: true,
      movement: null,
      action: null,
      statuses: stone.capturing == null ? [] : ["capturing"],
    }));

    const projectiles: SampledEntity[] = this.world.projectiles.map((projectile) => ({
      id: `court-projectile-${projectile.id}`,
      label: projectile.abilityId,
      kind: "prop",
      archetype: "projectile",
      team: teamName(projectile.team),
      position: scenePosition(projectile.pos.x, projectile.pos.y, 0.45),
      scale: 0.28,
      visible: true,
      movement: { gait: "dash", progress: this.time % 1 },
      action: null,
      statuses: [],
    }));

    const zones: SampledEntity[] = this.world.zones.map((zone) => ({
      id: `court-zone-${zone.id}`,
      label: zone.kind,
      kind: "zone",
      archetype: "zone",
      team: teamName(zone.team),
      position: scenePosition(zone.pos.x, zone.pos.y),
      scale: Math.max(0.5, zone.radius * WORLD_SCALE),
      visible: true,
      movement: null,
      action: null,
      statuses: [zone.kind],
    }));

    return [...cover, ...stones, ...zones, ...projectiles, ...units];
  }

  private activeAction(unit: Unit): ActiveAction | null {
    if (unit.casting) {
      const progress = 1 - Math.max(0, unit.casting.remaining) / Math.max(0.001, unit.casting.total);
      return {
        eventId: `court-cast-${unit.id}-${unit.casting.abilityId}`,
        action: unit.casting.abilityId,
        targetId: unit.casting.targetId == null ? undefined : this.unitId(unit.casting.targetId),
        phase: "telegraph",
        phaseProgress: progress,
        totalProgress: progress,
        tags: tagsFor(unit, unit.casting.abilityId),
      };
    }
    if (unit.charging) {
      const progress = Math.min(1, unit.charging.traveled / Math.max(0.001, unit.charging.maxDist));
      return {
        eventId: `court-charge-${unit.id}-${unit.charging.abilityId}`,
        action: unit.charging.abilityId,
        phase: "commit",
        phaseProgress: progress,
        totalProgress: progress,
        tags: tagsFor(unit, unit.charging.abilityId),
      };
    }
    return null;
  }

  private sampleImpacts(): ActiveImpact[] {
    return this.world.events.flatMap((event) => {
      if (event.age > 0.42 || !["dmg", "crit", "heal", "block", "miss", "kd"].includes(event.kind)) return [];
      const target = this.nearestUnit(event.x, event.y);
      if (!target) return [];
      let id = this.eventIds.get(event);
      if (!id) {
        id = this.nextEventId(event.kind);
        this.eventIds.set(event, id);
      }
      const source = this.nearestSource(target, event.kind === "heal");
      return [{
        eventId: id,
        sourceId: this.unitId(source?.id ?? target.id),
        targetId: this.unitId(target.id),
        outcome: event.kind === "heal" ? "heal" as const : event.kind === "block" ? "block" as const : event.kind === "miss" ? "miss" as const : "hit" as const,
        amount: event.value || undefined,
        effect: event.text || event.kind,
        age: event.age,
      }];
    });
  }
}
